/**
 * Analytics Page
 *
 * Displays algorithm insights, exploration patterns, and listening statistics.
 */
import React, { useState, useEffect } from 'react';
import { getToken } from '@/lib/auth';

/**
 * Safe fetch for analytics - doesn't redirect on 401
 * (Spotify token expiry shouldn't log user out of the app)
 */
async function safeFetch(endpoint: string): Promise<Response | null> {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const token = getToken();

  if (!token) return null;

  try {
    const response = await fetch(`${apiUrl}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch {
    return null;
  }
}

interface AlgorithmPerformance {
  tracks_recommended: number;
  tracks_completed: number;
  avg_completion_rate: number;
  success_rate: number;
}

interface AlgorithmStats {
  algorithm_performance: Record<string, AlgorithmPerformance>;
  best_algorithm: string;
  overall_satisfaction: number;
}

interface ExplorationInsights {
  insights: {
    total_discoveries: number;
    discovery_success_rate: number;
    best_discovery_times: string[];
    genre_exploration_breadth: number;
    recommended_exploration_rate: number;
  };
  current_exploration_rate: number;
  current_context: {
    time_of_day: string;
    day_of_week: string;
    is_weekend: boolean;
  };
}

interface GenreDistribution {
  genre: string;
  count: number;
}

interface TopGenresData {
  top_genres: string[];
  genre_distribution: GenreDistribution[];
}

const ALGORITHM_LABELS: Record<string, { name: string; icon: string; description: string }> = {
  personalized_mood: {
    name: 'Mood Engine',
    icon: '🎭',
    description: 'Tailored to your mood preferences'
  },
  collaborative: {
    name: 'Collaborative',
    icon: '👥',
    description: 'Based on similar listeners'
  },
  ai_personalized: {
    name: 'AI Recommendations',
    icon: '🤖',
    description: 'AI-powered personalization'
  },
  exploration: {
    name: 'Discovery',
    icon: '🔍',
    description: 'New artists and genres'
  }
};

const Analytics: React.FC = () => {
  const [algorithmStats, setAlgorithmStats] = useState<AlgorithmStats | null>(null);
  const [explorationInsights, setExplorationInsights] = useState<ExplorationInsights | null>(null);
  const [topGenres, setTopGenres] = useState<TopGenresData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);

    // Fetch each endpoint separately to handle partial failures gracefully
    // Using safeFetch to avoid logout on Spotify token expiry

    const statsRes = await safeFetch('/api/hybrid-recommendations/algorithm-stats');
    if (statsRes?.ok) {
      const data = await statsRes.json();
      setAlgorithmStats(data);
    }

    const insightsRes = await safeFetch('/api/hybrid-recommendations/exploration-insights');
    if (insightsRes?.ok) {
      const data = await insightsRes.json();
      setExplorationInsights(data);
    }

    const genresRes = await safeFetch('/api/recommendations/top-genres?limit=10');
    if (genresRes?.ok) {
      const data = await genresRes.json();
      setTopGenres(data);
    }

    setLoading(false);
  };

  const formatPercentage = (value: number): string => {
    return `${Math.round(value * 100)}%`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Analytics</h1>
          <p className="text-gray-400">Your music discovery insights</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-6 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/2 mb-4"></div>
              <div className="h-20 bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Analytics</h1>
        <p className="text-gray-400">Understand how your music recommendations work</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <QuickStatCard
          label="Overall Match"
          value={algorithmStats ? formatPercentage(algorithmStats.overall_satisfaction) : '--'}
          icon="✨"
          color="green"
        />
        <QuickStatCard
          label="Exploration Rate"
          value={explorationInsights ? formatPercentage(explorationInsights.current_exploration_rate) : '--'}
          icon="🔍"
          color="blue"
        />
        <QuickStatCard
          label="Top Genres"
          value={topGenres?.top_genres?.length?.toString() || '--'}
          icon="🎵"
          color="purple"
        />
        <QuickStatCard
          label="Best Algorithm"
          value={algorithmStats?.best_algorithm ? ALGORITHM_LABELS[algorithmStats.best_algorithm]?.name || 'AI' : '--'}
          icon="🏆"
          color="yellow"
        />
      </div>

      {/* Algorithm Performance */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span>🤖</span> Algorithm Performance
        </h2>
        <p className="text-gray-400 text-sm mb-6">How different recommendation engines perform for you</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {algorithmStats?.algorithm_performance &&
            Object.entries(algorithmStats.algorithm_performance).map(([key, perf]) => {
              const config = ALGORITHM_LABELS[key] || { name: key, icon: '📊', description: '' };
              const isBest = key === algorithmStats.best_algorithm;

              return (
                <div
                  key={key}
                  className={`p-4 rounded-lg border transition-all ${
                    isBest
                      ? 'bg-spotify-green/10 border-spotify-green/40'
                      : 'bg-gray-800/50 border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{config.icon}</span>
                      <div>
                        <h3 className="font-medium text-white">{config.name}</h3>
                        <p className="text-xs text-gray-500">{config.description}</p>
                      </div>
                    </div>
                    {isBest && (
                      <span className="text-xs bg-spotify-green/20 text-spotify-green px-2 py-1 rounded-full">
                        Best Match
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Success Rate</span>
                      <span className="text-white font-medium">{formatPercentage(perf.success_rate)}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          isBest ? 'bg-spotify-green' : 'bg-gray-500'
                        }`}
                        style={{ width: `${perf.success_rate * 100}%` }}
                      />
                    </div>

                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>{perf.tracks_completed}/{perf.tracks_recommended} completed</span>
                      <span>{formatPercentage(perf.avg_completion_rate)} avg</span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Exploration Insights */}
      {explorationInsights && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>🔍</span> Exploration Insights
          </h2>
          <p className="text-gray-400 text-sm mb-6">Your music discovery patterns and preferences</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Context */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Current Context</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {explorationInsights.current_context.time_of_day === 'morning' ? '🌅' :
                     explorationInsights.current_context.time_of_day === 'afternoon' ? '☀️' :
                     explorationInsights.current_context.time_of_day === 'evening' ? '🌆' : '🌙'}
                  </span>
                  <span className="text-white capitalize">{explorationInsights.current_context.time_of_day}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{explorationInsights.current_context.is_weekend ? '🎉' : '📅'}</span>
                  <span className="text-white capitalize">{explorationInsights.current_context.day_of_week}</span>
                </div>
              </div>
            </div>

            {/* Discovery Stats */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Discovery Stats</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Discovery Rate</span>
                    <span className="text-spotify-green font-medium">
                      {formatPercentage(explorationInsights.current_exploration_rate)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div
                      className="bg-spotify-green h-1.5 rounded-full"
                      style={{ width: `${explorationInsights.current_exploration_rate * 100}%` }}
                    />
                  </div>
                </div>
                {explorationInsights.insights && (
                  <p className="text-xs text-gray-500">
                    {explorationInsights.insights.total_discoveries} new artists discovered
                  </p>
                )}
              </div>
            </div>

            {/* Recommended Settings */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Recommendations</h3>
              <div className="space-y-2 text-sm">
                {explorationInsights.insights?.best_discovery_times?.length > 0 ? (
                  <p className="text-gray-400">
                    Best discovery time: <span className="text-white">{explorationInsights.insights.best_discovery_times[0]}</span>
                  </p>
                ) : (
                  <p className="text-gray-400">
                    Suggested rate: <span className="text-white">{formatPercentage(explorationInsights.insights?.recommended_exploration_rate || 0.2)}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Genres */}
      {topGenres && topGenres.genre_distribution && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>🎵</span> Your Top Genres
          </h2>
          <p className="text-gray-400 text-sm mb-6">Based on your listening history</p>

          <div className="space-y-3">
            {topGenres.genre_distribution.slice(0, 8).map((genre, index) => {
              const maxCount = topGenres.genre_distribution[0]?.count || 1;
              const percentage = (genre.count / maxCount) * 100;

              return (
                <div key={genre.genre} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-5">{index + 1}.</span>
                      <span className="text-white font-medium capitalize">{genre.genre}</span>
                    </div>
                    <span className="text-sm text-gray-400">{genre.count} artists</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2 ml-7">
                    <div
                      className="bg-gradient-to-r from-spotify-green to-green-400 h-2 rounded-full transition-all group-hover:opacity-80"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {topGenres.top_genres && topGenres.top_genres.length > 8 && (
            <p className="text-sm text-gray-500 mt-4 ml-7">
              + {topGenres.top_genres.length - 8} more genres
            </p>
          )}
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={loadAnalytics}
          className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <span>🔄</span> Refresh Analytics
        </button>
      </div>
    </div>
  );
};

interface QuickStatCardProps {
  label: string;
  value: string;
  icon: string;
  color: 'green' | 'blue' | 'purple' | 'yellow';
}

const QuickStatCard: React.FC<QuickStatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-3 md:p-4`}>
      <div className="flex items-center justify-between mb-1 md:mb-2">
        <span className="text-xl md:text-2xl">{icon}</span>
      </div>
      <p className="text-xl md:text-2xl font-bold text-white">{value}</p>
      <p className="text-xs md:text-sm text-gray-400">{label}</p>
    </div>
  );
};

export default Analytics;
